/**
 * Single shared @tanstack/react-query client for the web app.
 *
 * Defaults are deliberately conservative for an internal-tooling SaaS:
 *  - `staleTime: 30s` so navigating between tabs doesn't refetch instantly
 *  - `gcTime: 5min` keeps results in memory long enough for back/forward
 *  - `retry: 1` for queries — one network blip shouldn't show an error
 *  - `refetchOnWindowFocus: true` (Shane, 2026-07-18) — server-side flows
 *    (O&M folder feeds, filed-output refreshes) change data behind open
 *    views, so clicking back into the window refreshes what you're
 *    looking at instead of needing an app reload. staleTime still damps
 *    rapid-fire refetches.
 *
 * `ApiError` instances thrown by `apiFetch` carry typed codes — and until
 * 2026-09-23 this file went on to say "react-query's default error handling is
 * enough — we don't need a global onError hook for now". It is not, and the
 * default for a mutation with no onError is to swallow the error: the button
 * was pressed, the request was refused, and the screen did not change.
 *
 * The sub-contract audit found 28 of 31 mutations in that module with no
 * onError, which made nine role-gated Delete buttons silently do nothing for
 * anyone who was not a director or manager. The MutationCache below catches
 * every one of them, product-wide, so a refusal is seen instead of retried.
 */

import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import { recordMutationError, showsItsOwnError } from './mutationErrors';
import { reportFaultFromError } from './faultReporter';
import { ApiError } from './api';

/**
 * A load is tried again only when it never got the platform's answer: no
 * connection, a reply cut off, the proxy's empty reply while the API restarts,
 * or a server error. The platform's own "no" (a 4xx) is an answer, not a load
 * problem, so it is never retried.
 */
export function retryLoadProblem(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  // A queryFn calling fetch itself sees a dropped connection as a TypeError.
  if (error instanceof TypeError) return true;
  if (!(error instanceof ApiError)) return false;
  return error.reachedPlatform === false || error.status >= 500;
}

/** 1 s, 2 s, then 4 s between tries. */
export function loadRetryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 8000);
}

export const queryClient = new QueryClient({
  /*
   * Runs IN ADDITION to a mutation's own onError, not instead of it - so the
   * three mutations that already handle their own errors keep doing so, and
   * the rest stop failing in silence.
   *
   * UNLESS THE MUTATION ASKS TO BE LEFT OUT (2026-10-14). "Said twice beats
   * never said" is the right trade across the staff product. It is the wrong
   * one on the Employee app, where a mistyped NI number drew the app's own
   * plain-English sentence AND a blocking platform dialog quoting
   * "INVALID_NI_NUMBER · PUT /api/me/ni-number" at an employee on their own
   * phone. `meta: SHOWS_ITS_OWN_ERROR` opts a call out; mutationErrors.ts
   * says why that is tagged on the mutation rather than sniffed from the URL.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (showsItsOwnError(mutation.options.meta)) return;
      recordMutationError(error);
    },
  }),
  /*
   * A LOAD THAT FAILED, for the Error Log (2026-10-02). Not the error box -
   * each screen already shows its own "Failed to load", and a box on top of it
   * would say it twice. Only a fault the browser alone can see is sent: the
   * server unreachable, or a reply that was not the platform's. A screen
   * polling a dead server is one queued entry with a count, not a flood.
   */
  queryCache: new QueryCache({
    onError: (error) => { reportFaultFromError(error, 'load'); },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: retryLoadProblem,
      retryDelay: loadRetryDelay,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 0,
    },
  },
});
